/**
 * What a session re-scope (POST /auth/switch-context) does to the cached
 * location selector lists.
 *
 * An account's location list (GET /accounts/:id/locations) depends on the
 * account and on the caller's permissions at the token's location — which
 * locations they may see (locations:read, account:admin, user management)
 * — while its active-job counts depend only on the user. So after a location
 * switch that stays in the same account and lands on the same permission set,
 * the cached list is still exactly what the server would answer.
 *
 * Invalidating it anyway was a second GET of the same list on nearly every
 * page load: the header's location is synced to the session at startup
 * (useSessionLocationSync), and that switch re-fetched the list it had just
 * loaded. A switch that changes the account or the permissions still
 * refreshes both lists.
 */
import type { QueryClient } from '@tanstack/react-query';
import { queryKeys } from './queryKeys';

/** True when both lists hold the same permission keys, in any order. */
export function samePermissionSet(a: readonly string[] | null | undefined, b: readonly string[] | null | undefined): boolean {
  const left = new Set(a ?? []);
  const right = new Set(b ?? []);
  if (left.size !== right.size) return false;
  for (const key of left) {
    if (!right.has(key)) return false;
  }
  return true;
}

export interface LocationSwitchScope {
  /** The account the header showed before the switch. */
  previousAccountId: string | null | undefined;
  /** The account the server moved the session to. */
  accountId: string;
  /** Permissions held before the switch. */
  previousPermissions: readonly string[] | null | undefined;
  /** Permissions the switch returned. */
  permissions: readonly string[];
}

/**
 * Refreshes the location selector lists after a location switch, but only
 * when the switch changed what they would contain. Returns whether it did.
 */
export function refreshLocationListsAfterSwitch(queryClient: QueryClient, scope: LocationSwitchScope): boolean {
  const unchanged = scope.previousAccountId === scope.accountId && samePermissionSet(scope.previousPermissions, scope.permissions);
  if (unchanged) return false;
  void queryClient.invalidateQueries({ queryKey: queryKeys.locations.byAccount(scope.accountId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.locations.mine });
  return true;
}
